import { FormEvent, useEffect, useState } from "react";
import { kpsiGet, kpsiPost } from "./kpsiApi";

type Candidate = { id: number; email?: string; firstName: string; lastName: string };
type Perm = { rightTimeUserId: number; scorer: boolean; interpreter: boolean };

const TABS = ["Basic Info", "HR Info", "Billing Info", "Permissions"] as const;

interface Props {
  id?: number;
  onBack: () => void;
  onSaved: () => void;
}

export default function KpsiCompanyEdit({ id, onBack, onSaved }: Props) {
  const isEdit = !!id;
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [comments, setComments] = useState("");
  const [hrName, setHrName] = useState("");
  const [hrEmail, setHrEmail] = useState("");
  const [hrPhone, setHrPhone] = useState("");
  const [hrFax, setHrFax] = useState("");
  const [hrCell, setHrCell] = useState("");
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingPhone, setBillingPhone] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [perms, setPerms] = useState<Record<number, { scorer: boolean; interpreter: boolean }>>({});
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    void kpsiGet<{
      company: Record<string, string | number | null>;
      permissions: Perm[];
      candidateUsers: Candidate[];
    }>(`/Kpsi/Companies/${id}`)
      .then((data) => {
        const c = data.company;
        setName(String(c.name ?? ""));
        setAddress(String(c.address ?? ""));
        setComments(String(c.comments ?? ""));
        setHrName(String(c.hrName ?? ""));
        setHrEmail(String(c.hrEmail ?? ""));
        setHrPhone(String(c.hrPhone ?? ""));
        setHrFax(String(c.hrFax ?? ""));
        setHrCell(String(c.hrCell ?? ""));
        setBillingName(String(c.billingName ?? ""));
        setBillingEmail(String(c.billingEmail ?? ""));
        setBillingPhone(String(c.billingPhone ?? ""));
        setBillingAddress(String(c.billingAddress ?? ""));
        setCandidates(data.candidateUsers ?? []);
        const map: Record<number, { scorer: boolean; interpreter: boolean }> = {};
        for (const p of data.permissions ?? []) {
          map[p.rightTimeUserId] = { scorer: !!p.scorer, interpreter: !!p.interpreter };
        }
        setPerms(map);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [id]);

  function toggle(userId: number, field: "scorer" | "interpreter") {
    setPerms((prev) => {
      const cur = prev[userId] ?? { scorer: false, interpreter: false };
      return { ...prev, [userId]: { ...cur, [field]: !cur[field] } };
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await kpsiPost("/Kpsi/Companies", {
        id: id ?? 0,
        name,
        address,
        comments,
        hrName,
        hrEmail,
        hrPhone,
        hrFax,
        hrCell,
        billingName,
        billingEmail,
        billingPhone,
        billingAddress,
        permissions: Object.entries(perms).map(([uid, p]) => ({
          rightTimeUserId: Number(uid),
          scorer: p.scorer,
          interpreter: p.interpreter,
        })),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  function goNext() {
    setTab((t) => Math.min(t + 1, TABS.length - 1));
  }

  return (
    <>
      <p>
        <button type="button" className="kpsi-link-btn" onClick={onBack}>
          &laquo; Back to companies
        </button>
      </p>
      <h1>{isEdit ? "Edit a Company" : "Add a New Company"}</h1>
      <p className="kpsi-muted">
        Required items are marked with a <span className="kpsi-required">*</span>.
      </p>
      {error && <div className="kpsi-alert">{error}</div>}

      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="kpsi-tabs-bar">
          <div className="kpsi-tabs">
            {TABS.map((label, i) => (
              <button
                key={label}
                type="button"
                className={tab === i ? "active" : ""}
                onClick={() => setTab(i)}
              >
                {label}
              </button>
            ))}
          </div>
          <button type="submit" className="kpsi-btn-primary">
            {isEdit ? "Update" : "Add"} &raquo;
          </button>
        </div>

        <div className="kpsi-tab-pane">
          {tab === 0 && (
            <>
              <div className="kpsi-form-stack">
                <div className="kpsi-field">
                  <label>
                    Company Name <span className="kpsi-required">*</span>
                  </label>
                  <input value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="kpsi-field">
                  <label>
                    Address <span className="kpsi-required">*</span>
                  </label>
                  <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={4} required />
                </div>
                <div className="kpsi-field">
                  <label>Comments</label>
                  <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={4} />
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 1 && (
            <>
              <div className="kpsi-form-grid kpsi-form-grid-hr">
                <div className="kpsi-field">
                  <label>Name</label>
                  <input value={hrName} onChange={(e) => setHrName(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Email</label>
                  <input type="email" value={hrEmail} onChange={(e) => setHrEmail(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Phone</label>
                  <input value={hrPhone} onChange={(e) => setHrPhone(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Fax</label>
                  <input value={hrFax} onChange={(e) => setHrFax(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Cell</label>
                  <input value={hrCell} onChange={(e) => setHrCell(e.target.value)} />
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 2 && (
            <>
              <div className="kpsi-form-grid kpsi-form-grid-billing">
                <div className="kpsi-field">
                  <label>Name</label>
                  <input value={billingName} onChange={(e) => setBillingName(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Email</label>
                  <input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} />
                </div>
                <div className="kpsi-field">
                  <label>Phone</label>
                  <input value={billingPhone} onChange={(e) => setBillingPhone(e.target.value)} />
                </div>
                <div className="kpsi-field kpsi-field-full">
                  <label>Address</label>
                  <textarea value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} rows={4} />
                </div>
              </div>
              <button type="button" className="kpsi-btn-secondary" onClick={goNext}>
                Next
              </button>
            </>
          )}

          {tab === 3 && (
            <div className="kpsi-perm-columns">
              <div>
                <h2 className="h6">Interpreters</h2>
                <div className="kpsi-checkbox-list">
                  {candidates.map((u) => {
                    const p = perms[u.id] ?? { scorer: false, interpreter: false };
                    return (
                      <label key={`i-${u.id}`}>
                        <input
                          type="checkbox"
                          checked={p.interpreter}
                          onChange={() => toggle(u.id, "interpreter")}
                        />{" "}
                        {u.firstName} {u.lastName}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <h2 className="h6">Scorers</h2>
                <div className="kpsi-checkbox-list">
                  {candidates.map((u) => {
                    const p = perms[u.id] ?? { scorer: false, interpreter: false };
                    return (
                      <label key={`s-${u.id}`}>
                        <input
                          type="checkbox"
                          checked={p.scorer}
                          onChange={() => toggle(u.id, "scorer")}
                        />{" "}
                        {u.firstName} {u.lastName}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
}
